import React, { useState } from 'react';
import { portfolioItems } from '../data/mock';
import { X, ChevronLeft, ChevronRight } from 'lucide-react';
import { Dialog, DialogContent } from './ui/dialog';

const categories = ['All', 'Bags', 'Belts', 'Technical Development'];

// Helper function to check if an item is a technical/design image
const isTechnicalImage = (item) => {
  if (!item) return false;
  
  const technicalCategories = ['Technical Development', 'Sketches', 'Tech Pack'];
  const technicalKeywords = ['tech pack', 'drawing', 'technical', 'spec', 'sketch'];
  
  // Check category
  if (technicalCategories.some(cat => 
    item.category?.toLowerCase().includes(cat.toLowerCase())
  )) {
    return true;
  }
  
  // Check title for keywords
  const titleLower = item.title?.toLowerCase() || '';
  if (technicalKeywords.some(keyword => titleLower.includes(keyword))) {
    return true;
  }
  
  return false;
};

const PortfolioSection = () => {
  const [activeCategory, setActiveCategory] = useState('All');
  const [selectedItem, setSelectedItem] = useState(null);

  const filteredItems =
    activeCategory === 'All'
      ? portfolioItems
      : portfolioItems.filter((item) => item.category === activeCategory);

  const handlePrevious = () => {
    const currentIndex = filteredItems.findIndex(
      (item) => item.id === selectedItem?.id
    );
    if (currentIndex > 0) {
      setSelectedItem(filteredItems[currentIndex - 1]);
    }
  };

  const handleNext = () => {
    const currentIndex = filteredItems.findIndex(
      (item) => item.id === selectedItem?.id
    );
    if (currentIndex < filteredItems.length - 1) {
      setSelectedItem(filteredItems[currentIndex + 1]);
    }
  };

  // Disable right-click context menu for image protection
  const handleContextMenu = (e) => {
    e.preventDefault();
    return false;
  };

  return (
    <section id="portfolio" className="py-24 lg:py-32 bg-[#FAF8F5]">
      <div className="max-w-7xl mx-auto px-6 lg:px-12">
        {/* Section Header */}
        <div className="text-center mb-12">
          <span className="font-inter text-xs tracking-[0.3em] uppercase text-[#8B7355]">
            Selected Work
          </span>
          <h2 className="font-playfair text-3xl md:text-4xl lg:text-5xl font-light text-[#3D3D3D] mt-4">
            Portfolio
          </h2>
          <div className="w-12 h-[1px] bg-[#8B7355] mx-auto mt-6" />
        </div>

        {/* Category Filter */}
        <div className="flex flex-wrap justify-center gap-4 mb-12">
          {categories.map((category) => (
            <button
              key={category}
              onClick={() => setActiveCategory(category)}
              className={`font-inter text-xs tracking-[0.15em] uppercase px-6 py-3 transition-all duration-300 ${
                activeCategory === category
                  ? 'bg-[#3D3D3D] text-[#FAF8F5]'
                  : 'bg-transparent text-[#5A5A5A] border border-[#C4B5A4] hover:bg-[#3D3D3D] hover:text-[#FAF8F5] hover:border-[#3D3D3D]'
              }`}
            >
              {category}
            </button>
          ))}
        </div>

        {/* Portfolio Grid with Image Protection */}
        <div 
          className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6"
          onContextMenu={handleContextMenu}
        >
          {filteredItems.map((item) => (
            <div
              key={item.id}
              className="cursor-pointer group relative overflow-hidden aspect-[4/5]"
              onClick={() => setSelectedItem(item)}
              onContextMenu={handleContextMenu}
            >
              {/* Image with drag disabled */}
              <img
                src={item.image}
                alt={item.title}
                className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105 select-none pointer-events-none"
                draggable="false"
                onDragStart={(e) => e.preventDefault()}
              />
              
              {/* Watermark Overlay */}
              <div className="absolute bottom-3 right-3 z-10 pointer-events-none">
                <span className="font-inter text-[10px] tracking-wider text-white/60 bg-black/30 px-2 py-1 backdrop-blur-sm">
                  © Divyanka Karmakar
                </span>
              </div>

              {/* Hover Overlay */}
              <div className="absolute inset-0 bg-[#3D3D3D]/0 group-hover:bg-[#3D3D3D]/70 transition-all duration-500 flex items-end justify-start p-6">
                <div className="translate-y-8 opacity-0 group-hover:translate-y-0 group-hover:opacity-100 transition-all duration-500">
                  <span className="font-inter text-xs tracking-widest uppercase text-[#C4B5A4] block mb-1">
                    {item.category}
                  </span>
                  <h3 className="font-playfair text-lg text-[#FAF8F5]">
                    {item.title}
                  </h3>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Case Study Modal */}
      <Dialog open={!!selectedItem} onOpenChange={() => setSelectedItem(null)}>
        <DialogContent className="max-w-4xl bg-[#FAF8F5] border-none p-0 overflow-hidden">
          <button
            onClick={() => setSelectedItem(null)}
            className="absolute top-4 right-4 z-50 w-10 h-10 flex items-center justify-center bg-[#3D3D3D] text-[#FAF8F5] hover:bg-[#5A5A5A] transition-colors"
            aria-label="Close modal"
          >
            <X size={20} />
          </button>

          {selectedItem && (
            <div className="grid md:grid-cols-2" onContextMenu={handleContextMenu}>
              {/* Image - Conditional styling based on content type */}
              <div className={`relative aspect-square md:aspect-auto min-h-[300px] md:min-h-[400px] ${
                isTechnicalImage(selectedItem) ? 'bg-white' : 'bg-[#F5F1EC]'
              }`}>
                <img
                  src={selectedItem.image}
                  alt={selectedItem.title}
                  className={`w-full h-full select-none ${
                    isTechnicalImage(selectedItem) 
                      ? 'object-contain p-4' 
                      : 'object-cover'
                  }`}
                  draggable="false"
                  onDragStart={(e) => e.preventDefault()}
                />
                {/* Watermark on modal image */}
                <div className="absolute bottom-4 right-4 z-10 pointer-events-none">
                  <span className="font-inter text-xs tracking-wider text-white/60 bg-black/30 px-2 py-1 backdrop-blur-sm">
                    © Divyanka Karmakar
                  </span>
                </div>
                {/* Navigation */}
                <div className="absolute bottom-4 left-4 flex gap-2">
                  <button
                    onClick={handlePrevious}
                    className="w-10 h-10 flex items-center justify-center bg-[#FAF8F5]/90 text-[#3D3D3D] hover:bg-[#FAF8F5] transition-colors disabled:opacity-50"
                    disabled={
                      filteredItems.findIndex(
                        (item) => item.id === selectedItem?.id
                      ) === 0
                    }
                  >
                    <ChevronLeft size={20} />
                  </button>
                  <button
                    onClick={handleNext}
                    className="w-10 h-10 flex items-center justify-center bg-[#FAF8F5]/90 text-[#3D3D3D] hover:bg-[#FAF8F5] transition-colors disabled:opacity-50"
                    disabled={
                      filteredItems.findIndex(
                        (item) => item.id === selectedItem?.id
                      ) ===
                      filteredItems.length - 1
                    }
                  >
                    <ChevronRight size={20} />
                  </button>
                </div>
              </div>

              {/* Content */}
              <div className="p-8 lg:p-10 flex flex-col justify-center">
                <span className="font-inter text-xs tracking-[0.2em] uppercase text-[#8B7355] mb-2">
                  {selectedItem.category}
                </span>
                <h3 className="font-playfair text-2xl lg:text-3xl text-[#3D3D3D] mb-4">
                  {selectedItem.title}
                </h3>
                <p className="font-inter text-sm text-[#5A5A5A] leading-relaxed mb-6">
                  {selectedItem.description}
                </p>

                <div className="space-y-3 pt-6 border-t border-[#E8E2DB]">
                  <div className="flex justify-between">
                    <span className="font-inter text-xs tracking-widest uppercase text-[#8B7355]">
                      Client
                    </span>
                    <span className="font-inter text-sm text-[#3D3D3D]">
                      {selectedItem.client}
                    </span>
                  </div>
                  <div className="flex justify-between">
                    <span className="font-inter text-xs tracking-widest uppercase text-[#8B7355]">
                      Year
                    </span>
                    <span className="font-inter text-sm text-[#3D3D3D]">
                      {selectedItem.year}
                    </span>
                  </div>
                </div>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </section>
  );
};

export default PortfolioSection;
